import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Checkbox, notification } from "antd";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import styled from "styled-components";
import { useCheckTermsOfUseQuery, useAcceptTermsOfUseMutation } from "store/api/api";

const StyledTermsOfServiceModal = styled.div`
	p,
	li {
		margin-bottom: 24px;
	}
	.ant-checkbox-wrapper {
		align-items: flex-start;
	}
	.ant-checkbox {
		align-self: flex-start;
		margin-top: 8px;
	}
	.checkbox {
		margin-bottom: 16px;
	}
`;

const TermsOfServiceModal = ({ checked, setChecked, isDashboard }) => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const { data: termsOfUse, isLoading, refetch } = useCheckTermsOfUseQuery();
	const [acceptTermsOfUse, { isLoading: loading }] = useAcceptTermsOfUseMutation();

	const primaryColor = usePrimaryColor();

	useEffect(() => {
		if (isDashboard) {
			setIsModalVisible(!isLoading && !termsOfUse?.data);
		}
	}, [isDashboard, isLoading, termsOfUse?.data]);

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleOk = async () => {
		if (isDashboard && checked) {
			try {
				await acceptTermsOfUse().unwrap();
				await refetch();
				setIsModalVisible(false);
			} catch (error) {
				notification.error({
					message: error?.data?.message || "Failed to accept terms of service.",
				});
			}
		} else {
			setIsModalVisible(false);
		}
	};

	const handleCancel = () => {
		setIsModalVisible(false);
	};

	if (isDashboard) {
		if (!isModalVisible) {
			return null;
		}
	}
	return (
		<>
			{!isDashboard ? (
				<Typography.Text strong style={{ color: primaryColor, cursor: "pointer" }} onClick={showModal}>
					Terms of Service
				</Typography.Text>
			) : null}
			<Modal
				className="modalTerms"
				styles={{
					body: { height: "430px", overflowY: "auto" },
					footer: { marginTop: "32px" },
					header: { titleMarginBottom: "16px" },
				}}
				open={isModalVisible}
				onOk={handleOk}
				onCancel={handleCancel}
				title={<p style={{ fontSize: "24px", fontWeight: 500, lineHeight: 2 }}>Terms of Service</p>}
				maskClosable={false}
				closable={!isDashboard}
				footer={[
					<Button
						className="primary-filled--branding-primary-color"
						style={{
							backgroundColor: checked ? primaryColor : "#ccc",
							borderColor: checked ? primaryColor : "#ccc",
							color: "#fff",
						}}
						key="close"
						onClick={handleOk}
						disabled={!checked}
						data-id="btnAcceptTerms"
						loading={loading}
					>
						Continue
					</Button>,
				]}
				destroyOnClose
				data-id="termModal"
			>
				<StyledTermsOfServiceModal>
					<p>
						By using the SWEATBASE platform and participating in any digital workouts, video content, or fitness
						programs available through the platform, you agree to the following:
					</p>
					<ol style={{ listStyle: "none" }}>
						<li>
							<strong>1. Assumption of Risk:</strong>
							<br />
							You acknowledge that engaging in physical exercise involves inherent risks, including the possibility of
							injury, illness, or other medical conditions. You voluntarily assume full responsibility for your
							participation and any associated risks.
						</li>
						<li>
							<strong>2. Medical Clearance:</strong>
							<br />
							You affirm that you are in good physical condition and do not suffer from any condition that would prevent
							or limit your participation in physical activity. If you have any medical concerns, you are responsible
							for consulting a licensed healthcare provider before using the platform.
						</li>
						<li>
							<strong>3. Release of Liability:</strong>
							<br />
							To the fullest extent permitted by law, you release and discharge SWEATBASE, its parent company,
							affiliates, content providers, trainers, gym partners, employees, and agents from any and all liability,
							claims, damages, or demands arising out of or in connection with your participation in the services
							offered through SWEATBASE.
						</li>
						<li>
							<strong>4. No Live Supervision Disclaimer:</strong>
							<br />
							SWEATBASE provides on-demand, pre-recorded, or otherwise unsupervised fitness content. You acknowledge
							that you are responsible for exercising safely and modifying or ceasing activity as needed based on your
							fitness level and health status.
						</li>
					</ol>

					<Checkbox className="checkbox" onChange={(e) => setChecked(e.target.checked)} checked={checked} data-id="chkTermsAgree">
						By continuing to use SWEATBASE, you affirm that you have read, understood, and agreed to this waiver and the
						associated Terms of Service.
					</Checkbox>
				</StyledTermsOfServiceModal>
			</Modal>
		</>
	);
};

export default TermsOfServiceModal;

